import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

const getToken = () => {
  return localStorage.getItem('token');
};

const resourcesApiClient = axios.create({
  baseURL: config.api.resourcesServiceUrl,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 600000 // 10 minutes - increased for async KB query polling
});

resourcesApiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(resourcesApiClient);

// Proactively refresh token before any request if within 30-minute expiry window
applyTokenRefreshInterceptor(resourcesApiClient);

const resourcesService = {
  // Fetch available resource categories
  fetchCategories: async () => {
    try {
      const response = await resourcesApiClient.get('/resources/categories');
      return response.data;
    } catch (error) {
      console.error('Error fetching resource categories:', error);
      throw error;
    }
  },

  // Upload a file to a specific category
  uploadFile: async (file, categoryId, description = null, tags = [], onProgress) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category_id', categoryId);
      
      if (description) {
        formData.append('description', description);
      }
      
      if (tags.length > 0) {
        formData.append('tags', tags.join(','));
      }
      
      const response = await resourcesApiClient.post('/resources/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error uploading file to resources:', error);
      throw error;
    }
  },

  // Upload multiple files
  uploadFiles: async (files, categoryId, onProgress) => {
    const results = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        // Call onProgress with overall progress info
        if (onProgress) {
          onProgress({
            fileIndex: i,
            totalFiles: files.length,
            currentFileName: file.name,
            overallProgress: Math.round((i / files.length) * 100)
          });
        }
        
        const result = await resourcesService.uploadFile(file, categoryId, null, [], (fileProgress) => {
          // Call onProgress with file-specific progress
          if (onProgress) {
            onProgress({
              fileIndex: i,
              totalFiles: files.length,
              currentFileName: file.name,
              fileProgress: fileProgress,
              overallProgress: Math.round(((i + (fileProgress / 100)) / files.length) * 100)
            });
          }
        });
        
        results.push({ 
          success: true, 
          filename: file.name, 
          result 
        });
        
      } catch (error) {
        console.error(`Upload failed for ${file.name}:`, error);
        results.push({ 
          success: false, 
          filename: file.name, 
          error: error.message 
        });
      }
    }
    
    return results;
  },

  // Query the knowledge base for a specific category
  queryKnowledgeBase: async ({ categoryId, question, sessionId }) => {
    try {
      const payload = {
        category_id: categoryId,
        question: question,
        session_id: sessionId || undefined
      };
      
      // Initial query - returns job_id immediately
      const response = await resourcesApiClient.post('/resources/query', payload);
      const initialData = response.data;
      
      // Check if this is an async response (empty answer with job_id)
      if (!initialData.answer && initialData.job_id) {
        const jobId = initialData.job_id;
        console.log(`Query job started: ${jobId}, polling for completion...`);
        
        // Poll for completion (max 9 minutes)
        const maxPollTime = 540000; // 9 minutes in ms
        const pollInterval = 2000; // 2 seconds
        const startTime = Date.now();
        
        while ((Date.now() - startTime) < maxPollTime) {
          await new Promise(resolve => setTimeout(resolve, pollInterval));
          
          // Check job status
          const statusResponse = await resourcesApiClient.get(`/resources/query-status/${jobId}`);
          const statusData = statusResponse.data;
          
          // If answer is populated and job_id is null, job is complete
          if (statusData.answer && !statusData.job_id) {
            console.log(`Query job ${jobId} completed successfully`);
            return statusData;
          }
          
          // If answer is populated but it's an error message
          if (statusData.answer && statusData.answer.includes('error occurred')) {
            console.error(`Query job ${jobId} failed`);
            return statusData;
          }
          
          // Continue polling...
          console.debug(`Query job ${jobId} still processing...`);
        }
        
        // Timeout after max poll time
        throw new Error('Query timed out after 9 minutes');
      }
      
      // If answer is already present, return immediately (sync response or cached)
      return initialData;
    } catch (error) {
      console.error('Error querying knowledge base:', error);
      throw error;
    }
  },

  // Get all resources for a specific category
  getResourcesByCategory: async (categoryId) => {
    try {
      const response = await resourcesApiClient.get(`/resources/categories/${categoryId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching resources by category:', error);
      throw error;
    }
  },

  // Download a file from resources (streaming through backend)
  downloadFile: async (filename, categoryId, convertToPdf = false) => {
    try {
      const response = await resourcesApiClient.get('/resources/download', {
        params: {
          filename: filename,
          category_id: categoryId,
          convert_to_pdf: convertToPdf
        },
        responseType: 'blob'
      });
      
      return {
        blob: response.data,
        headers: response.headers
      };
    } catch (error) {
      console.error('Error downloading file from resources:', error);
      throw error;
    }
  },

  // Create a new category
  createCategory: async (categoryData) => {
    try {
      const response = await resourcesApiClient.post('/resources/categories', categoryData);
      return response.data;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  },

  // Update an existing category
  updateCategory: async (categoryId, categoryData) => {
    try {
      const response = await resourcesApiClient.put(`/resources/categories/${categoryId}`, categoryData);
      return response.data;
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  },

  // Delete a category
  deleteCategory: async (categoryId) => {
    try {
      const response = await resourcesApiClient.delete(`/resources/categories/${categoryId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }
};

export default resourcesService;